import dayjs from 'dayjs';

import {
  IAssetsResponseFROMApi,
  IBusinessAssetPayload,
  ICashAssetPayload,
  IInvestmentAssetPayload,
  IOtherAssetPayload,
  IPensionAssetPayload,
  IPropertyAssetPayload,
} from '@zeta-types';
import { getCAPIntObject } from '../common/commons';

export const mapAssetsResponseToSchema = (response: IAssetsResponseFROMApi) => {
  const mapAssetFields = (assets: any[], assetType: string) => {
    switch (assetType) {
      case 'bankAccountDetails':
        return assets?.map((asset) => ({
          id: asset.id || '',
          institution: asset.institutionName || '',
          accountType: asset.accountType || '',
          accountNumber: asset.accountNumber || '',
          amount: asset.amount?.toString() || '',
          interestRate: asset.interestRate || null,
          assetDescription: asset.description || '',
          show: true,
        }));

      case 'investmentAssetDetails':
        return assets?.map((asset) => ({
          id: asset.id || '',
          institution: asset.institutionName || '',
          investmentType: asset.investmentType || '',
          accountNumber: asset.accountNumber || '',
          startDate: asset.startTime || '',
          endDate: asset.endTime || '',
          value: asset.amount?.toString() || '',
          assetDescription: asset.description || '',
          show: true,
        }));

      case 'businessAssetDetails':
        return assets?.map((asset) => ({
          id: asset.id || '',
          companyName: asset.companyName || '',
          location: asset.location || '',
          shareholderPercentage: asset.percentage?.toString() || null,
          capOrInt: getCAPIntObject(asset.capOrInt),
          incomeAmount: asset.incomeAmount?.toString() || '',
          interestRate: asset.interestRate || null,
          value: asset.currentValue?.toString() || '',
          assetDescription: asset.description || '',
          show: true,
        }));

      case 'pensionAssetDetails':
        return assets?.map((asset) => ({
          id: asset.id || '',
          pensionName: asset.pensionName || '',
          pensionType: asset.pensionType || '',
          accountNumber: asset.accountNumber || '',
          provider: asset.providerName || '',
          cetv: asset.cetv || null,
          transferValue: asset.transferValue || null,
          startDate: asset.startDate || '',
          endDate: asset.endDate || '',
          assetLocation: asset.assetLocation || '',
          scheme: asset.scheme || '',
          policyNumber: asset.policyNumber || '',
          assetDescription: asset.description || '',
          show: true,
        }));

      case 'propertyAssetDetails':
        return assets?.map((asset) => ({
          id: asset.id || '',
          propertyName: asset.propertyName || '',
          propertyType: asset.propertyType || '',
          location: asset.location || '',
          income: asset.incomeAmount?.toString() || '',
          capOrInt: getCAPIntObject(asset.capOrInt),
          interestRate: asset.interestRate || null,
          startDate: asset.startDate || '',
          endDate: asset.endDate || '',
          currentValue: asset.currentValue?.toString() || '',
          assetDescription: asset.description || '',
          show: true,
        }));

      case 'otherAssetDetails':
        return assets?.map((asset) => ({
          id: asset.id || '',
          assetType: asset.assetType || '',
          location: asset.location || '',
          income: asset.incomeAmount?.toString() || '',
          capOrInt: getCAPIntObject(asset.capOrInt),
          interestRate: asset.interestRate || null,
          startDate: asset.startDate || '',
          endDate: asset.endDate || '',
          currentValue: asset.currentValue?.toString() || '',
          assetDescription: asset.description || '',
          show: true,
        }));

      default:
        return [];
    }
  };

  const mapAssetsDetails = (details: any) => ({
    cashAssets: mapAssetFields(details?.bankAccountDetails?.clientBankAssets || [], 'bankAccountDetails'),
    investmentAssets: mapAssetFields(details?.investmentAssetDetails?.clientInvestmentAssets || [], 'investmentAssetDetails'),
    businessAssets: mapAssetFields(details?.businessAssetDetails?.clientBusinessAssets || [], 'businessAssetDetails'),
    pensionAssets: mapAssetFields(details?.pensionAssetDetails?.clientPensionAssets || [], 'pensionAssetDetails'),
    propertyAssets: mapAssetFields(details?.propertyAssetDetails?.clientPropertyAssets || [], 'propertyAssetDetails'),
    otherAssets: mapAssetFields(details?.otherAssetDetails?.clientOtherAssets || [], 'otherAssetDetails'),
  });

  return {
    client: mapAssetsDetails(response?.assets),
    partner: {
      cashAssets: mapAssetFields(response?.assets?.bankAccountDetails?.partnerBankAssets || [], 'bankAccountDetails'),
      investmentAssets: mapAssetFields(response?.assets?.investmentAssetDetails?.partnerInvestmentAssets || [], 'investmentAssetDetails'),
      businessAssets: mapAssetFields(response?.assets?.businessAssetDetails?.partnerBusinessAssets || [], 'businessAssetDetails'),
      pensionAssets: mapAssetFields(response?.assets?.pensionAssetDetails?.partnerPensionAssets || [], 'pensionAssetDetails'),
      propertyAssets: mapAssetFields(response?.assets?.propertyAssetDetails?.partnerPropertyAssets || [], 'propertyAssetDetails'),
      otherAssets: mapAssetFields(response?.assets?.otherAssetDetails?.partnerOtherAssets || [], 'otherAssetDetails'),
    },
    joint: {
      cashAssets: mapAssetFields(response?.assets?.bankAccountDetails?.jointBankAssets || [], 'bankAccountDetails'),
      investmentAssets: mapAssetFields(response?.assets?.investmentAssetDetails?.jointInvestmentAssets || [], 'investmentAssetDetails'),
      businessAssets: mapAssetFields(response?.assets?.businessAssetDetails?.jointBusinessAssets || [], 'businessAssetDetails'),
      pensionAssets: mapAssetFields(response?.assets?.pensionAssetDetails?.jointPensionAssets || [], 'pensionAssetDetails'),
      propertyAssets: mapAssetFields(response?.assets?.propertyAssetDetails?.jointPropertyAssets || [], 'propertyAssetDetails'),
      otherAssets: mapAssetFields(response?.assets?.otherAssetDetails?.jointOtherAssets || [], 'otherAssetDetails'),
    },
  };
};

export const mapToPayloadAssets = (assetsData: any) => {
  const mapCashAssets = (assets: any[]): ICashAssetPayload[] =>
    assets?.map((asset) => ({
      id: asset.id || '',
      institutionName: asset.institution,
      accountType: asset.accountType,
      accountNumber: asset.accountNumber,
      currency: 'GBP',
      amount: asset.amount || 0,
      interestRate: asset.interestRate || 0,
      description: asset.assetDescription,
    }));

  const mapInvestmentAssets = (assets: any[]): IInvestmentAssetPayload[] =>
    assets?.map((asset) => ({
      id: asset.id || '',
      institutionName: asset.institution,
      investmentType: asset.investmentType,
      accountNumber: asset.accountNumber,
      startTime: dayjs(asset.startDate).isValid() ? dayjs(asset.startDate).format('YYYY-MM-DD') : '',
      endTime: dayjs(asset.endDate).isValid() ? dayjs(asset.endDate).format('YYYY-MM-DD') : '',
      currency: 'GBP',
      amount: asset.value || 0,
      description: asset.assetDescription,
    }));

  const mapBusinessAssets = (assets: any[]): IBusinessAssetPayload[] =>
    assets?.map((asset) => ({
      id: asset.id || '',
      companyName: asset.companyName,
      location: asset.location,
      percentage: asset.shareholderPercentage || 0,
      capOrInt: asset.capOrInt.value,
      incomeAmount: asset.incomeAmount || 0,
      interestRate: asset.interestRate || 0,
      currentValue: asset.value || 0,
      description: asset.assetDescription,
      currency: 'GBP',
    }));

  const mapPensionAssets = (assets: any[]): IPensionAssetPayload[] =>
    assets?.map((asset) => ({
      id: asset.id || '',
      pensionName: asset.pensionName,
      pensionType: asset.pensionType,
      accountNumber: asset.accountNumber,
      providerName: asset.provider,
      cetv: asset.cetv || 0,
      transferValue: asset.transferValue || 0,
      startDate: dayjs(asset.startDate).isValid() ? dayjs(asset.startDate).format('YYYY-MM-DD') : '',
      endDate: dayjs(asset.endDate).isValid() ? dayjs(asset.endDate).format('YYYY-MM-DD') : '',
      assetLocation: asset.assetLocation,
      scheme: asset.scheme,
      policyNumber: asset.policyNumber,
      description: asset.assetDescription,
      currency: 'GBP',
    }));

  const mapPropertyAssets = (assets: any[]): IPropertyAssetPayload[] =>
    assets?.map((asset) => ({
      id: asset.id || '',
      propertyName: asset.propertyName,
      propertyType: asset.propertyType,
      location: asset.location,
      incomeAmount: asset.income || 0,
      capOrInt: asset.capOrInt.value,
      interestRate: asset.interestRate || 0,
      startDate: dayjs(asset.startDate).isValid() ? dayjs(asset.startDate).format('YYYY-MM-DD') : '',
      endDate: dayjs(asset.endDate).isValid() ? dayjs(asset.endDate).format('YYYY-MM-DD') : '',
      currentValue: asset.currentValue || 0,
      description: asset.assetDescription,
      currency: 'GBP',
    }));

  const mapOtherAssets = (assets: any[]): IOtherAssetPayload[] =>
    assets?.map((asset) => ({
      id: asset.id || '',
      assetType: asset.assetType,
      location: asset.location,
      incomeAmount: asset.income || 0,
      capOrInt: asset.capOrInt.value,
      interestRate: asset.interestRate || 0,
      startDate: dayjs(asset.startDate).isValid() ? dayjs(asset.startDate).format('YYYY-MM-DD') : '',
      endDate: dayjs(asset.endDate).isValid() ? dayjs(asset.endDate).format('YYYY-MM-DD') : '',
      currentValue: asset.currentValue || 0,
      description: asset.assetDescription,
      currency: 'GBP',
    }));

  return {
    bankAccountDetails: {
      clientBankAssets: mapCashAssets(assetsData.client.cashAssets),
      partnerBankAssets: mapCashAssets(assetsData.partner.cashAssets),
      jointBankAssets: mapCashAssets(assetsData.joint.cashAssets),
    },
    investmentAssetDetails: {
      clientInvestmentAssets: mapInvestmentAssets(assetsData.client.investmentAssets),
      partnerInvestmentAssets: mapInvestmentAssets(assetsData.partner.investmentAssets),
      jointInvestmentAssets: mapInvestmentAssets(assetsData.joint.investmentAssets),
    },
    businessAssetDetails: {
      clientBusinessAssets: mapBusinessAssets(assetsData.client.businessAssets),
      partnerBusinessAssets: mapBusinessAssets(assetsData.partner.businessAssets),
      jointBusinessAssets: mapBusinessAssets(assetsData.joint.businessAssets),
    },
    pensionAssetDetails: {
      clientPensionAssets: mapPensionAssets(assetsData.client.pensionAssets),
      partnerPensionAssets: mapPensionAssets(assetsData.partner.pensionAssets),
      jointPensionAssets: mapPensionAssets(assetsData.joint.pensionAssets),
    },
    propertyAssetDetails: {
      clientPropertyAssets: mapPropertyAssets(assetsData.client.propertyAssets),
      partnerPropertyAssets: mapPropertyAssets(assetsData.partner.propertyAssets),
      jointPropertyAssets: mapPropertyAssets(assetsData.joint.propertyAssets),
    },
    otherAssetDetails: {
      clientOtherAssets: mapOtherAssets(assetsData.client.otherAssets),
      partnerOtherAssets: mapOtherAssets(assetsData.partner.otherAssets),
      jointOtherAssets: mapOtherAssets(assetsData.joint.otherAssets),
    },
  };
};
